import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useFetcher } from "~/hooks/useFetcher";
import {
  TURN_PARAM,
  turnAnchorId,
} from "~/pages/manifold-nextgen/components/conversations/useAnchoredScroll";
import { nextgenConversationPath } from "~/pages/manifold-nextgen/paths";
import {
  memoryPath,
  memorySourcePath,
  type Memory,
  type MemorySource,
  type PartialMemory,
} from "~/services/memories";

interface MemoryDetailDialogProps {
  memory: PartialMemory | null;
  onClose: () => void;
}

/**
 * Read-only view of one memory's full text. The registry list carries everything
 * but `content`, so opening a memory fetches it by id — the uncounted read, which
 * leaves the agent-usage counters alone.
 */
export const MemoryDetailDialog = ({ memory, onClose }: MemoryDetailDialogProps) => {
  const { data, isLoading, error } = useFetcher<Memory>(memory ? memoryPath(memory.id) : null);

  // The service 404s unless the conversation and the turn both still exist, so a
  // resolved source is the licence to render a link. A memory with no turn has no
  // provenance to resolve — `MemoryCreate` rejects a turn without a conversation,
  // so the turn alone answers it — and asking would be a request certain to 404.
  const { data: source } = useFetcher<MemorySource>(
    memory?.reference_turn_id != null ? memorySourcePath(memory.id) : null
  );

  return (
    <Dialog open={memory !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{memory?.name}</DialogTitle>
          <DialogDescription>{memory?.description}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="text-sm text-destructive">Unable to load this memory.</p>
        ) : isLoading || !data ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-foreground">{data.content}</p>
        )}

        {memory && (
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>
              Remembered{" "}
              {formatDistanceToNowStrict(new Date(memory.created_at), { addSuffix: true })}
              {" · "}
              Used {memory.total_invocations} {memory.total_invocations === 1 ? "time" : "times"}
            </p>
            {source && (
              <Link
                to={`${nextgenConversationPath(source.project_id, source.conversation_id)}?${TURN_PARAM}=${turnAnchorId(source.turn_id)}`}
                className="flex shrink-0 items-center gap-1 font-medium hover:text-foreground hover:underline"
              >
                View the turn
                <ArrowUpRight className="size-3" aria-hidden />
              </Link>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
